import { Box, Button, Stack, Title, Tooltip } from "@mantine/core";
import { ReactElement, useState } from "react";

type ButtonsSelectProps = {
  value: any;
  numberOfColumns?: number;
  align?: "left" | "center" | "right";
  data:
    | {
        value: any;
        label: string | ReactElement;
        subLabel?: string;
        description?: string;
      }[]
    | {
        group?: string;
        items: {
          value: any;
          label: string | ReactElement;
          description?: string;
        }[];
      }[];
  onChange: (value: any) => void;
};

export const ButtonsSelect = ({
  data,
  numberOfColumns = 2,
  align = "center",
  value,
  onChange,
}: ButtonsSelectProps) => {
  const [val, setVal] = useState(value);
  const isGrouped = data.length > 0 && "items" in data[0];

  if (!isGrouped) {
    return (
      <Box
        style={theme => ({
          display: "grid",
          gridAutoRows:
            numberOfColumns === 1 ? "minmax(80px, auto)" : undefined,
          gridRowGap: theme.spacing.sm,
          gridTemplateColumns:
            numberOfColumns === 3
              ? "1fr 1fr 1fr"
              : numberOfColumns === 1
                ? "1fr"
                : "1fr 1fr",
          minHeight: "30%",
          minWidth: "30%",
          alignContent: "center",
          justifyContent: "center",
        })}
      >
        {data.map(item => (
          <Tooltip
            key={item.value}
            label={item.description}
            position="bottom"
            disabled={!item.description}
          >
            <Button
              variant="light"
              onClick={() => {
                onChange(item.value);
                setVal(item.value);
              }}
              data-testid={`button-select-${item.value}`}
              value={val}
              color={item.value === value ? "blue" : "gray"}
              p="xs"
              m={5}
              bd={item.value === value ? "2px solid blue.6" : "0"}
              styles={
                item.subLabel
                  ? {
                      root: { height: "auto" },
                      label: {
                        whiteSpace: "normal",
                        height: "auto",
                        display: "block",
                        textBoxTrim: "none",
                        overflow: "visible",
                      },
                    }
                  : undefined
              }
              style={() => ({
                minHeight: "100%",
                display: "flex",
                justifyContent:
                  align === "center"
                    ? "center"
                    : align === "left"
                      ? "flex-start"
                      : "flex-end",
                textAlign: align,
              })}
            >
              {item.subLabel ? (
                <Box component="span" display="block" lh={1.25}>
                  {item.label}
                  <br />
                  {item.subLabel}
                </Box>
              ) : (
                item.label
              )}
            </Button>
          </Tooltip>
        ))}
      </Box>
    );
  }

  return (
    <Stack gap="lg">
      {data.map((groupData, groupIndex) => (
        <Box key={groupIndex}>
          {groupData.group && (
            <Title order={6} mb="xs" fw={600}>
              {groupData.group}
            </Title>
          )}
          <Box
            style={theme => ({
              display: "grid",
              gridRowGap: theme.spacing.sm,
              gridColumnGap: theme.spacing.sm,
              gridTemplateColumns:
                numberOfColumns === 3
                  ? "1fr 1fr 1fr"
                  : numberOfColumns === 1
                    ? "1fr"
                    : "1fr 1fr",
              alignContent: "center",
              justifyContent: "center",
              marginBottom: theme.spacing.md,
            })}
          >
            {groupData.items?.map(item => (
              <Tooltip
                key={item.value}
                label={item.description}
                position="bottom"
                disabled={!item.description}
              >
                <Button
                  variant="light"
                  onClick={() => {
                    onChange(item.value);
                    setVal(item.value);
                  }}
                  data-testid={`button-select-${item.value}`}
                  value={val}
                  color={item.value === value ? "blue" : "gray"}
                  p="xs"
                  m={5}
                  bd={item.value === value ? "2px solid blue.6" : "0"}
                  style={() => ({
                    height: "auto",
                    minHeight: "44px",
                    display: "flex",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    justifyContent:
                      align === "center"
                        ? "center"
                        : align === "left"
                          ? "flex-start"
                          : "flex-end",
                    textAlign: align,
                  })}
                >
                  {item.label}
                </Button>
              </Tooltip>
            ))}
          </Box>
        </Box>
      ))}
    </Stack>
  );
};
